import { Nullable } from '@/scripts/models/utility-types';
import { Model } from '@viddsee/server-api-client';
import Vue from 'vue';
import { validateAnswer } from './Question/validator';

type QuestionInfo = Model.FilmReviewQuestion.Info;

const isFilled = (x: any[] | null): x is any[] => !!x && x.length > 0;

export type QuestionType = Model.FilmReviewQuestion.Info['type'];

export type QuestionDetails = Partial<Model.FilmReviewQuestion.Details>;

const generateDefaultForType = (type: QuestionType, originalValue: QuestionDetails | null): QuestionDetails => {
  const defaultDetails = ((): QuestionDetails => {
    switch (type) {
      case 'checkbox':
      case 'radio': {
        return {
          options: [],
        };
      }
      case 'range': {
        return {
          maximumValue: 5,
        };
      }
      case 'textbox':
      default: {
        return {};
      }
    }
  })();

  return {
    ...defaultDetails,
    ...originalValue,
  };
};
export class Question {
  readonly questions: Map<number, Question>;

  readonly id: number;

  readonly uri: string;

  readonly actionUrl: Nullable<string>;

  readonly details: QuestionDetails;

  readonly label: Nullable<string>;

  readonly type: QuestionType;

  readonly nextInfo: Nullable<Model.FilmReviewQuestion.NextInfo[]>;

  private answerData: string[] | null = null;

  constructor(questionInfo: QuestionInfo, questions: Map<number, Question>) {
    this.questions = questions;

    this.id = questionInfo.id;
    this.uri = questionInfo.uri;
    this.actionUrl = questionInfo.actionUrl;
    this.details = generateDefaultForType(questionInfo.type, questionInfo.details);
    this.label = questionInfo.label;
    this.type = questionInfo.type;

    this.nextInfo = questionInfo.next;
    this.answerData = null;
  }

  get next(): Question | null {
    const next = this.nextInfo;
    if (!isFilled(next)) return null;

    const matchingNextInfo = next.find((nextInfo) => {
      const destination = this.questions.get(nextInfo.id);
      if (!destination) return false;

      if (!nextInfo.conditions) return true;

      return nextInfo.conditions.every((condition) => {
        const queryQuestion = this.questions.get(condition.targetQuestionId);

        if (!queryQuestion) return false;

        return validateAnswer(condition.comparator, queryQuestion.answers || [''], condition.comparatorValue);
      });
    });

    if (!matchingNextInfo) return null;

    return this.questions.get(matchingNextInfo.id) || null;
  }

  set answers(answer: string[] | null) {
    this.answerData = answer;
  }

  get answers(): string[] | null {
    return this.answerData;
  }
}

interface ObservableData {
  root: Question | null;
  current: Question | null;
}

/**
 * A questions class with Vue observable data
 */
export class Questions {
  private vm: ObservableData;

  constructor(questionInfos: QuestionInfo[]) {
    const questions = new Map<number, Question>();
    questionInfos.forEach((questionInfo) => {
      questions.set(questionInfo.id, new Question(questionInfo, questions));
    });

    const root = questions.get(questionInfos[0]?.id) || null;

    this.vm = Vue.observable<ObservableData>({
      // Always assume first element in the list to be the first question
      root,
      current: root,
    });
  }

  get root(): Question | null {
    return this.vm.root;
  }

  get current(): Question | null {
    return this.vm.current;
  }

  set current(newCurrent: Question | null) {
    this.vm.current = newCurrent;
  }
}
